import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { banksAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Bank as BankIcon, MagnifyingGlass } from '@phosphor-icons/react';

const ACCOUNT_TYPES = ['Bank', 'Cash', 'Credit Card', 'Other'];
const EMPTY = { account_name: '', bank_name: '', account_number: '', ifsc: '', account_type: 'Bank', opening_balance: 0, is_active: true };

const typeBadge = (t) => ({
  Bank: 'bg-blue-50 text-blue-700 border-blue-200',
  Cash: 'bg-amber-50 text-amber-700 border-amber-200',
  'Credit Card': 'bg-purple-50 text-purple-700 border-purple-200',
  Other: 'bg-gray-100 text-gray-600 border-gray-200',
}[t] || 'bg-gray-100 text-gray-600 border-gray-200');

export default function BanksPage() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await banksAPI.list({ include_inactive: true });
      setBanks(data.banks || []);
    } catch (e) { toast.error('Failed to load bank accounts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowDialog(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.account_name.trim()) { toast.error('Account name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, opening_balance: Number(form.opening_balance) || 0 };
      if (editing) { await banksAPI.update(editing.id, payload); toast.success('Bank account updated'); }
      else { await banksAPI.create(payload); toast.success('Bank account added'); }
      setShowDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await banksAPI.remove(id);
      toast.success(data.soft_deleted ? 'Marked inactive (has linked payments)' : 'Bank account removed');
      setDeleteConfirm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const handleSeed = async () => {
    try {
      const { data } = await banksAPI.seedFromPayments();
      toast.success(`Imported ${data.created} account(s) from existing payments`);
      load();
    } catch (e) { toast.error('Failed to import'); }
  };

  const filtered = banks.filter(b =>
    !search ||
    b.account_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.bank_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.account_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="banks-page">
      <PageHeader
        title="Banks & Accounts"
        subtitle="Manage your bank, cash and card accounts. These appear as the 'Deposit To' options when recording payments (like Zoho Books)."
      />

      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search accounts..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="banks-search" />
          </div>
          <Button variant="outline" onClick={handleSeed} data-testid="seed-banks-btn">Import from Payments</Button>
          <Button onClick={openCreate} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-bank-btn">
            <Plus size={16} className="mr-1.5" /> Add Account
          </Button>
        </div>

        <p className="text-sm text-[#64748B]">{filtered.length} account{filtered.length !== 1 ? 's' : ''}</p>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="banks-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
                <th className="text-left p-3">Account Name</th>
                <th className="text-left p-3">Bank</th>
                <th className="text-left p-3">Account No.</th>
                <th className="text-left p-3">IFSC</th>
                <th className="text-center p-3">Type</th>
                <th className="text-right p-3">Opening Balance</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
              ) : filtered.length > 0 ? (
                filtered.map((b) => (
                  <tr key={b.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`bank-row-${b.id}`}>
                    <td className="p-3 font-medium text-[#0F172A] flex items-center gap-2"><BankIcon size={15} className="text-[#64748B]" />{b.account_name}</td>
                    <td className="p-3 text-[#64748B]">{b.bank_name || '-'}</td>
                    <td className="p-3 text-[#64748B] font-mono text-xs">{b.account_number || '-'}</td>
                    <td className="p-3 text-[#64748B] font-mono text-xs">{b.ifsc || '-'}</td>
                    <td className="p-3 text-center"><span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${typeBadge(b.account_type)}`}>{b.account_type}</span></td>
                    <td className="p-3 text-right">₹{Number(b.opening_balance || 0).toLocaleString('en-IN')}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${b.is_active !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{b.is_active !== false ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-bank-${b.id}`}><PencilSimple size={16} /></Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteConfirm(b)} data-testid={`delete-bank-${b.id}`}><Trash size={16} /></Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">{search ? 'No matching accounts' : 'No accounts yet. Click "Add Account" or "Import from Payments".'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="bank-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">{editing ? 'Edit Account' : 'Add Account'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Account Name *</label>
              <Input value={form.account_name} onChange={(e) => setForm(f => ({ ...f, account_name: e.target.value }))} placeholder="e.g. HDFC Current A/C" data-testid="bank-name-input" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Account Type</label>
              <Select value={form.account_type} onValueChange={(v) => setForm(f => ({ ...f, account_type: v }))}>
                <SelectTrigger data-testid="bank-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Bank Name</label>
                <Input value={form.bank_name} onChange={(e) => setForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="e.g. HDFC Bank" data-testid="bank-bankname-input" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Opening Balance</label>
                <Input type="number" value={form.opening_balance} onChange={(e) => setForm(f => ({ ...f, opening_balance: e.target.value }))} data-testid="bank-opening-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Account Number</label>
                <Input value={form.account_number} onChange={(e) => setForm(f => ({ ...f, account_number: e.target.value }))} data-testid="bank-accnum-input" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">IFSC</label>
                <Input value={form.ifsc} onChange={(e) => setForm(f => ({ ...f, ifsc: e.target.value }))} data-testid="bank-ifsc-input" />
              </div>
            </div>
            {editing && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded border-gray-300" data-testid="bank-active-toggle" />
                <span className="text-sm text-[#0F172A]">Active</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="bank-save-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-bank-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Remove Account</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] py-2">Remove <strong>{deleteConfirm?.account_name}</strong>? If it has linked payments it will be marked inactive instead of deleted.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-bank-btn">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
